import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircle, LogOut, Settings, RefreshCw } from 'lucide-react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useServiceWorker } from '../contexts/ServiceWorkerContext';

/**
 * AppBarMenu Component
 * Reusable user profile menu with settings and logout options
 */
const AppBarMenu = ({ onLogout, isAdmin = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { updateAvailable, applyUpdate } = useServiceWorker();

  useEffect(() => {
    const loadUserEmail = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || '');
      } catch (error) {
        console.error('Error fetching user attributes:', error);
      }
    };
    loadUserEmail();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  const handleUpdate = () => {
    setIsOpen(false);
    applyUpdate();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-white hover:opacity-80 transition-opacity cursor-pointer relative"
        aria-label="user menu"
      >
        <UserCircle size={32} />
        {updateAvailable && (
          <span className="absolute top-1 right-1 w-3 h-3 bg-white rounded-full border-2 border-success animate-pulse" />
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-60 bg-surface border border-border rounded-lg shadow-lg z-50">
          <div className="px-4 py-3 border-b border-border">
            {userEmail && (
              <p className="text-sm text-text-muted truncate">{userEmail}</p>
            )}
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded ${
              isAdmin
                ? 'bg-primary/20 text-primary'
                : 'bg-secondary/20 text-secondary'
            }`}>
              {isAdmin ? t('menu.role_admin') : t('menu.role_user')}
            </span>
          </div>

          {/* Update Available */}
          {updateAvailable && (
            <button
              onClick={handleUpdate}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background transition-colors cursor-pointer text-left bg-success/10"
            >
              <RefreshCw size={20} className="text-success flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-success">{t('menu.update_available')}</p>
                <p className="text-xs text-text-muted">{t('menu.click_to_update')}</p>
              </div>
            </button>
          )}

          {/* Settings */}
          <button
            onClick={handleSettings}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background transition-colors cursor-pointer text-left"
          >
            <Settings size={20} className="text-secondary flex-shrink-0" />
            <p className="text-sm font-semibold text-secondary">{t('menu.settings')}</p>
          </button>

          <div className="border-t border-border">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background transition-colors cursor-pointer text-left"
            >
              <LogOut size={20} className="text-secondary flex-shrink-0" />
              <p className="text-sm font-semibold text-secondary">{t('menu.logout')}</p>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppBarMenu;
